<script setup lang="ts">
import { ref, watch } from 'vue';

import ButtonText from '@/shared/components/UI/Buttons/ButtonText.vue';
import Modal from '@/shared/components/UI/Modal.vue';
import { useTranslations } from '@/shared/composables/useTranslations';
import { domainMismatchNudge, userCookies } from '@/shared/constants/constants';
import {
  trackDomainMismatchNudgeAccepted,
  trackDomainMismatchNudgeDismissed
} from '@/shared/tracking/domain-mismatch-nudge';
import { setCookie } from '@/shared/utils/cookie';

const props = defineProps<{
  currentDomain: string;
  suggestedDomain: string;
  targetUrl: string;
  fallbackToHomepage: boolean;
}>();

const { t } = useTranslations();

const HOURS_PER_DAY = 24;

const isVisible = ref(true);
// Set once accept fires, so the watcher below doesn't also count the
// resulting close (navigating away sets isVisible false too) as a dismissal.
let hasAccepted = false;

watch(isVisible, (visible) => {
  if (visible || hasAccepted) {
    return;
  }

  setCookie(
    userCookies.DOMAIN_MISMATCH_SUPPRESSED,
    'true',
    domainMismatchNudge.DISMISSED_DAYS
  );
  trackDomainMismatchNudgeDismissed({
    current_domain: props.currentDomain,
    suggested_domain: props.suggestedDomain
  });
});

const onAccept = (): void => {
  hasAccepted = true;
  setCookie(
    userCookies.DOMAIN_MISMATCH_SUPPRESSED,
    'true',
    domainMismatchNudge.ACCEPTED_HOURS / HOURS_PER_DAY
  );
  trackDomainMismatchNudgeAccepted({
    from_domain: props.currentDomain,
    to_domain: props.suggestedDomain,
    target_url: props.targetUrl,
    fallback_to_homepage: props.fallbackToHomepage
  });
  window.location.href = props.targetUrl;
};
</script>

<template>
  <Modal v-model:active="isVisible" size="sm">
    <template #header>
      <h2 class="pr-12 text-xl font-bold text-gray-900">
        {{ t('domain_mismatch_nudge.title') }}
      </h2>
    </template>
    <template #body>
      <div class="flex flex-col gap-3">
        <p class="text-sm text-gray-600">
          {{
            t('domain_mismatch_nudge.description', { domain: suggestedDomain })
          }}
        </p>
        <ButtonText
          theme="primary"
          size="lg"
          class="mt-2 !text-lg"
          @click="onAccept"
        >
          {{
            t('domain_mismatch_nudge.switch_button', {
              domain: suggestedDomain
            })
          }}
        </ButtonText>
      </div>
    </template>
  </Modal>
</template>
